import React, { useState, useEffect, useMemo } from 'react';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { deleteProfileCatalogProduct, getProfileCatalogFilters, getCatalogProductExportUrl, searchProfileCatalogProducts } from '@/services/profile-catalog-products';
import { CatalogProductImportDialog } from './CatalogProductImportDialog';
import { CatalogProductList } from '../catalog-products/CatalogProductList';
import { Trans } from '@lingui/react/macro';
import { CatalogProductEditDialog } from '../catalog-products/CatalogProductEditDialog';
import { faBox, faBrush, faFileExport, faFileImport } from '@fortawesome/pro-light-svg-icons';
import { useAsyncState, useAsyncStateWithIsLoading } from '@/utils/hooks';
import { ProfileCatalogProduct, CatalogGroup, OptionalId } from '@/types';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import { CatalogGroupEditDialog } from '../catalog-groups/CatalogGroupEditDialog';
import { useParams } from 'react-router';
import { CatalogFilter, CatalogFilterValue } from './CatalogFilter';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';

type CatalogProductsProps = {};

export const CatalogProducts: React.FC<CatalogProductsProps> = () => {
    const profileId = useSelector((state: IState) => state.global.activeProfile!.id);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [checkedProducts, setCheckedProducts] = useState<ProfileCatalogProduct[]>([]);
    const [editingProductId, setEditingProductId] = useState<number | null>(null);
    const [isPaintProduct, setIsPaintProduct] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [defaultSelectedId, setDefaultSelected] = useState<number | undefined>(undefined);
    const [editingGroup, setEditingGroup] = useState<OptionalId<CatalogGroup> | null>(null);
    const [filter, setFilter] = useState<CatalogFilterValue>(CatalogFilterValue.empty());
    const [products, , reloadProducts, isLoading] = useAsyncStateWithIsLoading(
        () =>
            searchProfileCatalogProducts({
                ownerProfileId: profileId,
                itemCategoryIds: filter.itemCategoryIds,
                searchText: filter.searchText,
                suppliers: filter.suppliers.length === 0 ? null : filter.suppliers,
            }),
        [profileId, filter],
        null
    );

    const [filterData, , reloadFilterData] = useAsyncState(() => getProfileCatalogFilters(profileId), [profileId], null);

    let { id: paramId } = useParams<{ id: string }>();
    useEffect(() => {
        if (paramId && !isNaN(+paramId)) setDefaultSelected(+paramId);
        else setDefaultSelected(undefined);
    }, [paramId]);

    const handleCreateProductClick = () => {
        setEditingProductId(null);
        setIsPaintProduct(false);
        setShowEditDialog(true);
    };

    const handleCreatePaintProductClick = () => {
        setEditingProductId(null);
        setIsPaintProduct(true);
        setShowEditDialog(true);
    };

    const handleEditProductClick = (product: ProfileCatalogProduct) => {
        setIsDuplicating(false);
        setEditingProductId(product.id);
        setShowEditDialog(true);
    };

    const handleDuplicateProductClick = (product: ProfileCatalogProduct) => {
        setIsDuplicating(true);
        setEditingProductId(product.id);
        setShowEditDialog(true);
    };

    const handleEditProductDialogClose = () => {
        setIsDuplicating(false);
        setEditingProductId(null);
        setShowEditDialog(false);
    };

    const handleEditProductDialogSave = async () => {
        reloadProducts();
        reloadFilterData();
    };

    const handleDeleteClick = async (product: ProfileCatalogProduct) => {
        await deleteProfileCatalogProduct(product.id);
        reloadFilterData();
        reloadProducts();
    };

    const handleCreateGroupClick = (e) => {
        setEditingGroup({
            ownerProfileId: profileId,
            name: '',
            noneOptionDescription: '',
            noneOptionName: '',
            hasNoneOption: false,
        });
    };

    const handleEditGroupDialogClose = () => {
        setEditingGroup(null);
    };

    const handleEditGroupDialogSave = () => {
        setCheckedProducts([]);
    };

    // const handleCheckChange = (products: CatalogProduct[]) => {
    //     setCheckedProducts(products);
    // }

    if (!products || !filterData) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <ButtonBar>
                <Button variant="tertiary" onClick={handleCreateProductClick} icon={faBox}>
                    <Trans>Add a product</Trans>
                </Button>
                <Button variant="tertiary" onClick={handleCreatePaintProductClick} icon={faBrush}>
                    <Trans>Add custom paint product</Trans>
                </Button>
                <Button variant="tertiary" icon={faFileImport} onClick={() => setShowImportDialog(true)}>
                    <Trans>Import products</Trans>
                </Button>
                <Button as="a" href={getCatalogProductExportUrl(profileId)} target="_blank" variant="tertiary" icon={faFileExport}>
                    <Trans>Export products</Trans>
                </Button>

                {checkedProducts.length > 0 && (
                    <Button variant="primary" onClick={handleCreateGroupClick}>
                        <Trans>Group {checkedProducts.length} products</Trans>
                    </Button>
                )}

                <ButtonBar.Right>
                    <CatalogFilter isLoading={isLoading} filter={filter} onChange={setFilter} data={filterData} />
                </ButtonBar.Right>
            </ButtonBar>

            <CatalogProductEditDialog
                isCustomPaintProduct={isPaintProduct}
                isDuplicate={isDuplicating}
                show={showEditDialog}
                catalogProductId={editingProductId}
                onClose={handleEditProductDialogClose}
                onSave={handleEditProductDialogSave}
            />

            <CatalogProductImportDialog
                profileId={profileId}
                show={showImportDialog}
                onClose={() => setShowImportDialog(false)}
                onSave={() => {
                    setShowImportDialog(false);
                    reloadProducts();
                    reloadFilterData();
                }}
            />

            <CatalogGroupEditDialog
                show={!!editingGroup}
                group={editingGroup}
                onClose={handleEditGroupDialogClose}
                onSave={handleEditGroupDialogSave}
                productIds={checkedProducts.map((x) => x.id)}
            />

            <CatalogProductList
                defaultSelectedId={defaultSelectedId}
                products={products}
                onEditClick={handleEditProductClick}
                onDeleteClick={handleDeleteClick}
                onDuplicateClick={handleDuplicateProductClick}
                onCheckChange={(value) => setCheckedProducts(value)}
                checkedProductIds={checkedProducts.map((x) => x.id)}
            />
        </>
    );
};
